import { Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { useBooking } from "@/context/BookingContext";
import { ROYAL_IMG, RoyalCTA, RoyalLiquidBg } from "@/components/royal/shared";

// Royal Signature — Doctors: liquid full-bleed header + editorial profiles.
export default function DoctorsRoyal() {
  const { content } = useContent();
  const { book } = useBooking();
  const s = content?.settings || {};
  const doctors = content?.doctors || [];
  const heroBlur = parseInt(s.royal_hero_blur || "0", 10) || 0;
  const heroBg = s.royal_doctors_hero_image || doctors[0]?.image || ROYAL_IMG.portrait;

  return (
    <div className="bg-[#FAF8F5]" data-testid="doctors-page">
      <section className="relative flex min-h-[52vh] items-center overflow-hidden bg-[#F1EBE2]" data-testid="doctors-hero">
        <RoyalLiquidBg src={heroBg} blur={heroBlur} testid="doctors-hero-image" />
        <div className="relative mx-auto w-full max-w-[1400px] px-4 sm:px-8 lg:px-12">
          <div className="max-w-xl py-16 lg:py-20">
            <Reveal>
              <p className="eyebrow-royal" data-testid="doctors-eyebrow">Our Specialists</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-4 font-serif text-4xl font-light leading-[1.08] tracking-tight text-[#0F0F0F] sm:text-5xl lg:text-6xl" data-testid="doctors-title">
                Hands you can trust
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-sm font-normal leading-relaxed text-[#3A3630] sm:text-base">
                Every treatment at Royal Skin Clinic is led or overseen by a physician — artistry guided by medical science.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 pb-16 pt-12 sm:px-8 lg:px-12">
        <div className="space-y-10" data-testid="doctors-list">
          {doctors.map((d, i) => (
            <Reveal key={d.id} delay={0.05}>
              <article
                className={`royal-card grid items-center gap-10 rounded-[2rem] p-6 sm:p-10 lg:grid-cols-5 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
                data-testid={`doctor-card-${d.id}`}
              >
                <div className="lg:col-span-2">
                  <div className="overflow-hidden rounded-2xl">
                    <img src={d.image} alt={`Portrait of ${d.name}`} loading="lazy" className="aspect-[4/5] w-full object-cover" />
                  </div>
                </div>
                <div className="lg:col-span-3">
                  <p className="eyebrow-royal">{d.role}</p>
                  <h2 className="mt-3 font-serif text-3xl font-normal text-[#0F0F0F] sm:text-4xl">{d.name}</h2>
                  <p className="mt-5 max-w-xl text-sm font-normal leading-relaxed text-[#4A4A4A] sm:text-base">{d.bio}</p>
                  <ul className="mt-7 grid max-w-xl gap-3.5 sm:grid-cols-2">
                    {(d.specializations || []).map((sp) => (
                      <li key={sp} className="flex items-center gap-3 text-xs font-medium text-[#0F0F0F]">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F8F3E6]">
                          <Check className="h-3.5 w-3.5 text-[#C5A059]" aria-hidden="true" />
                        </span>
                        {sp}
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => book()} data-testid={`doctor-book-${d.id}`} className="btn-royal mt-8 px-7 py-3.5 text-[10px]">
                    Book with {d.name.replace(/^Dr\.?\s*/i, "").split(" ")[0]}
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
      <RoyalCTA />
    </div>
  );
}
